import { useState } from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { SearchCodeModalForm } from "src/components/common/form/search/SearchCodeModalForm";
import { SearchDropDownForm } from "src/components/common/form/search/SearchDropDownForm";
import { SearchMonthSingleForm } from "src/components/common/form/search/SearchMonthSingleForm";
import { SearchSectionForm } from "src/components/common/form/search/SearchSectionForm";
import { CompanyDto } from "src/dtos/company/companyDto";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";
import { defaultPRD1020_R01Filter, usePRD1020_R01Store } from "./Store_PRD1020_R01";

/**
 * 생산계획 대비 실적 조회조건을 편집하는 영역이다.
 * 드롭다운의 전체 선택은 빈 문자열로 정규화하며, 생산년월은 YYYY-MM으로 저장한다.
 * 품목 검색은 현재 브랜드·품목그룹·품목계정을 함께 전달해 후보 범위를 제한한다.
 */
export const SearchSection_PRD1020_R01 = () => {
	const { PRD1020_R01Filter, setPRD1020_R01Filter } = usePRD1020_R01Store();
	const { compCdList, brandCdList, itemGroupList, itemAccountList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const [pickerVisible, setPickerVisible] = useState(false);
	const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
	const itemCdFocusId = "itemCd_PRD1020_R01_search";

	/*
	 * 공통 품목 검색 이벤트가 itemCd와 itemNm을 한 번에 Store에 반영하도록 정의한다.
	 * Enter 자동완성과 더블클릭 검색 모달은 같은 extraFilter를 사용한다.
	 */
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: "itemCd",
			category: "품목",
			focusId: itemCdFocusId,
			responseMapping: {
				itemCd: "itemCd",
				itemNm: "itemNm",
			},
			searchFunction: async (searchText: string) => {
				// 입력한 품목코드와 정확히 일치하는 행만 자동 선택 후보로 반환한다.
				const response = await getItemPopupList(
					searchText,
					PRD1020_R01Filter.itemAccount || "all",
					PRD1020_R01Filter.itemGroup || "all",
					PRD1020_R01Filter.brandCd || "all",
				);
				return response.data.data.find((item) => item.itemCd === searchText);
			},
			// 품목 모달과 자동완성 조회가 현재 화면의 분류 조건을 공유하도록 매번 최신 값을 만든다.
			extraFilter: () => ({
				itemCd: PRD1020_R01Filter.itemCd || "",
				itemNm: PRD1020_R01Filter.itemNm || "",
				brandCd: PRD1020_R01Filter.brandCd || "all",
				itemGroup: PRD1020_R01Filter.itemGroup || "all",
				itemAccount: PRD1020_R01Filter.itemAccount || "all",
			}),
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: PRD1020_R01Filter,
		setEditData: setPRD1020_R01Filter,
		defaultData: defaultPRD1020_R01Filter,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: "PRD1020_R01_search",
	});

	return (
		<SearchSectionForm>
			{/* 전체 선택은 API에서 조건을 제외할 수 있도록 빈 문자열로 저장한다. */}
			<SearchDropDownForm<CompanyDto>
				label="사업장"
				value={PRD1020_R01Filter.compCd}
				optionList={compCdList}
				onChangeValue={(value) => setPRD1020_R01Filter({ compCd: value === "all" ? "" : value })}
			/>
			{/* 월 선택 결과는 백엔드 YearMonth 파싱 형식인 YYYY-MM으로 저장한다. */}
			<SearchMonthSingleForm
				label="생산년월"
				visible={pickerVisible}
				setVisible={setPickerVisible}
				value={new Date(PRD1020_R01Filter.yearMonth)}
				editable
				onChange={(date) => setPRD1020_R01Filter({
					yearMonth: `${formatDateYear4(date)}-${formatDateMonth(date)}`,
				})}
				pickerPosition={pickerPosition}
				setPickerPosition={setPickerPosition}
			/>
			<SearchDropDownForm<SysCodeDto>
				label="브랜드"
				value={PRD1020_R01Filter.brandCd}
				optionList={brandCdList}
				onChangeValue={(value) => setPRD1020_R01Filter({ brandCd: value === "all" ? "" : value })}
			/>
			<SearchDropDownForm<SysCodeDto>
				label="품목그룹"
				value={PRD1020_R01Filter.itemGroup}
				optionList={itemGroupList}
				onChangeValue={(value) => setPRD1020_R01Filter({ itemGroup: value === "all" ? "" : value })}
			/>
			<SearchDropDownForm<SysCodeDto>
				label="품목계정"
				value={PRD1020_R01Filter.itemAccount}
				optionList={itemAccountList}
				onChangeValue={(value) => setPRD1020_R01Filter({ itemAccount: value === "all" ? "" : value })}
			/>
			<SearchCodeModalForm
				label="품목코드"
				value={PRD1020_R01Filter.itemCd}
				valueLabel={PRD1020_R01Filter.itemNm}
				handleFoucsId={itemCdFocusId}
				onChange={getFieldHandlers("itemCd").onChange}
				onDoubleClick={getFieldHandlers("itemCd").onDoubleClick}
				onKeyDown={getFieldHandlers("itemCd").onEnterKeyDown}
			/>
		</SearchSectionForm>
	);
};
